export const RARO_INVEST_AMOUNT = 100;

export function referenceMonthOf(todayIso: string): string {
    return todayIso.slice(0, 7);
}

export function formatReferenceMonth(referenceMonth: string): string {
    const [year, month] = referenceMonth.split('-');
    const names = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    const index = Number(month) - 1;
    return names[index] ? `${names[index]} de ${year}` : referenceMonth;
}

export function formatBrl(amount: number): string {
    return `R$ ${amount.toFixed(2).replace('.', ',')}`;
}

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isReferenceMonth(value: unknown): value is string {
    return typeof value === 'string' && MONTH_PATTERN.test(value);
}

export function monthFromDate(value: unknown): string | null {
    if (typeof value === 'string') {
        const text = value.trim();
        const br = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(text);
        if (br) return `${br[3]}-${br[2]}`;
        const iso = /^(\d{4})-(\d{2})/.exec(text);
        return iso && isReferenceMonth(`${iso[1]}-${iso[2]}`) ? `${iso[1]}-${iso[2]}` : null;
    }
    const stamp = value as { toDate?: () => Date } | null;
    if (stamp && typeof stamp.toDate === 'function') {
        return monthFromDate(stamp.toDate().toISOString());
    }
    return null;
}

export function addMonths(month: string, amount: number): string {
    const [year, number] = month.split('-').map(Number);
    const index = year * 12 + (number - 1) + amount;
    const nextYear = Math.floor(index / 12);
    return `${nextYear}-${String((index % 12) + 1).padStart(2, '0')}`;
}

export const RARO_INVEST_BENEFIT_MONTHS = 12;
export const RARO_INVEST_MIN_INVESTMENT = 200;

export interface RaroInvestWindowInput {
    experienceEndMonth: string | null;
    countingMonths: readonly string[];
    currentMonth: string;
}

export const RARO_INVEST_NO_EXPERIENCE_MESSAGE =
    'O fim do seu período de experiência não está cadastrado. Fale com o DP.';

export function raroInvestWindow(input: RaroInvestWindowInput): { minMonth: string; maxMonth: string } | null {
    if (!input.experienceEndMonth) return null;
    const benefitEnd = addMonths(input.experienceEndMonth, RARO_INVEST_BENEFIT_MONTHS - 1);
    const maxMonth = benefitEnd < input.currentMonth ? benefitEnd : input.currentMonth;
    const latest = input.countingMonths.reduce<string | null>((max, month) => (max === null || month > max ? month : max), null);
    const minMonth = latest ? addMonths(latest, 1) : input.experienceEndMonth;
    return minMonth > maxMonth ? null : { minMonth, maxMonth };
}

export function listRaroInvestMonths(input: RaroInvestWindowInput): string[] {
    const window = raroInvestWindow(input);
    if (!window) return [];
    const months: string[] = [];
    for (let month = window.maxMonth; month >= window.minMonth; month = addMonths(month, -1)) {
        months.push(month);
    }
    return months;
}

export function validateRaroInvestMonth(month: unknown, input: RaroInvestWindowInput): string | null {
    if (!input.experienceEndMonth) return RARO_INVEST_NO_EXPERIENCE_MESSAGE;
    if (!isReferenceMonth(month)) return 'Escolha o mês de referência.';
    if (input.countingMonths.includes(month)) {
        return `Já existe um pedido do Raro Invest para ${formatReferenceMonth(month)}. Para reenviar, fale com o DP.`;
    }
    if (month > input.currentMonth) return 'Não é possível pedir o reembolso de um mês que ainda não chegou.';
    const benefitEnd = addMonths(input.experienceEndMonth, RARO_INVEST_BENEFIT_MONTHS - 1);
    if (month > benefitEnd) {
        return `O benefício vale por 12 meses a partir do fim da experiência, até ${formatReferenceMonth(benefitEnd)}. Para outros meses, fale com o DP.`;
    }
    const window = raroInvestWindow(input);
    if (!window) return 'Não há mês disponível para novo pedido. Em caso de dúvida, fale com o DP.';
    if (month < window.minMonth) {
        return `Escolha um mês a partir de ${formatReferenceMonth(window.minMonth)}. Para meses anteriores, fale com o DP.`;
    }
    return null;
}
